import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DarkPage } from "@/components/app/PageHeader";
import { Loader2, Flame, CalendarClock, Users, Archive } from "lucide-react";

const QUADRANTS = [
  { key: "do", label: "Do first", sub: "Important & urgent", icon: Flame, accent: "text-rose-400", border: "border-rose-500/30", bg: "bg-rose-500/5" },
  { key: "schedule", label: "Schedule", sub: "Important, not urgent", icon: CalendarClock, accent: "text-[#00F5D4]", border: "border-[#00F5D4]/30", bg: "bg-[#00F5D4]/5" },
  { key: "delegate", label: "Delegate", sub: "Urgent, not important", icon: Users, accent: "text-amber-400", border: "border-amber-500/30", bg: "bg-amber-500/5" },
  { key: "later", label: "Later", sub: "Neither important nor urgent", icon: Archive, accent: "text-[#7d8590]", border: "border-[#30363d]", bg: "bg-[#161b22]/50" },
];

const PRIORITY_BADGE = {
  low: "bg-[#1c2128] text-[#7d8590] border border-[#30363d]",
  medium: "bg-[#00F5D4]/10 text-[#00F5D4] border border-[#00F5D4]/20",
  high: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  critical: "bg-rose-500/10 text-rose-400 border border-rose-500/20",
};

function classify(t) {
  const important = t.priority === "high" || t.priority === "critical";
  const urgent = t.due_bucket === "overdue" || t.due_bucket === "today";
  if (important && urgent) return "do";
  if (important && !urgent) return "schedule";
  if (!important && urgent) return "delegate";
  return "later";
}

export default function TaskMatrix() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/tasks/my");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buckets = useMemo(() => {
    const b = { do: [], schedule: [], delegate: [], later: [] };
    for (const t of (data?.tasks || [])) {
      if (t.status === "done") continue;
      b[classify(t)].push(t);
    }
    return b;
  }, [data]);

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#00F5D4]" />
    </div>
  );

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Task Matrix</h1>
        <p className="text-sm text-[#7d8590] mt-1">Your open tasks, sorted into an Eisenhower matrix by priority and due date — no setup required.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {QUADRANTS.map(q => {
          const Icon = q.icon;
          const items = buckets[q.key];
          return (
            <div key={q.key} className={`border rounded-lg p-4 min-h-[220px] ${q.border} ${q.bg}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${q.accent}`} />
                  <div>
                    <div className={`text-sm font-semibold ${q.accent}`}>{q.label}</div>
                    <div className="text-[10px] text-[#7d8590] uppercase tracking-wide">{q.sub}</div>
                  </div>
                </div>
                <span className="text-xs text-[#7d8590] font-mono">{items.length}</span>
              </div>
              {items.length === 0 ? (
                <div className="text-xs text-[#4B5563] py-6 text-center">Nothing here.</div>
              ) : (
                <div className="space-y-1.5">
                  {items.map(t => (
                    <Link key={t.task_id} to={`/workspace/${t.project_id}`}
                      className="flex items-center justify-between gap-2 bg-[#0d1117] border border-[#21262d] rounded-md px-3 py-2 hover:border-[#00F5D4]/30 transition-colors">
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{t.title}</div>
                        {t.project?.name && <div className="text-[10px] text-[#7d8590] truncate">{t.project.name}</div>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {t.due_date && <span className="text-[10px] font-mono text-[#7d8590]">{t.due_date.slice(5)}</span>}
                        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm ${PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.medium}`}>{t.priority?.[0]?.toUpperCase()}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div></DarkPage>
  );
}
